/**
 * Everything that can sit in the Foundry's hold: ores, alloys and items.
 *
 * The numbers mirror Idle Planet Miner exactly (GDD §3): ore sell prices,
 * 1,000 ore per bar, bar and item recipes, smelt and craft times, sell prices
 * and the GC price to unlock each recipe. Only the names are VoidMiner's —
 * the items carry IdleVoid's Anomaly names and glyphs. The IPM name is kept
 * on each row so the mapping stays checkable against the wiki.
 *
 * Content covers IPM's first twenty-eight planets (through Palladium). Deeper
 * tiers follow the same pattern when the galaxy grows.
 */

export type ResourceKind = 'ore' | 'alloy' | 'item';
export type Rarity = 'common' | 'rare' | 'void';

/** Fleet-wide stats. Every bonus in the game is an additive % on one of these. */
export type FleetStat =
  | 'mine' | 'ship' | 'cargo'
  | 'smelt' | 'craft' | 'haste'
  | 'smeltYield' | 'craftYield' | 'discount'
  | 'sell' | 'alloyValue' | 'itemValue' | 'te';

export const FLEET_STAT_LABEL: Readonly<Record<FleetStat, string>> = {
  mine: 'Mining',
  ship: 'Ship speed',
  cargo: 'Cargo',
  smelt: 'Smelt speed',
  craft: 'Craft speed',
  haste: 'All production',
  smeltYield: 'Double smelt chance',
  craftYield: 'Double craft chance',
  discount: 'Planet cost reduction',
  sell: 'Sell price',
  alloyValue: 'Alloy value',
  itemValue: 'Item value',
  te: 'TE at Fold',
};

export interface Ingredient {
  readonly id: string;
  readonly qty: number;
}

export interface Recipe {
  readonly inputs: readonly Ingredient[];
  /** Seconds at speed ×1. */
  readonly time: number;
  /** GC to unlock the recipe for this galaxy (IPM's recipe price). 0 = free. */
  readonly unlock: number;
}

export interface ResourceDef {
  readonly id: string;
  readonly name: string;
  /** The IPM resource this one is. */
  readonly ipm: string;
  readonly kind: ResourceKind;
  /** Order within its kind; also the unlock order. */
  readonly tier: number;
  /** GC per unit before bonuses (IPM sell price). */
  readonly value: number;
  readonly color: string;
  readonly glyph: string;
  readonly rarity?: Rarity;
  readonly recipe?: Recipe;
  /** Item fitting effects (items only). */
  readonly fits?: readonly FleetStat[];
}

const ing = (id: string, qty: number): Ingredient => ({ id, qty });

// ── Ores ───────────────────────────────────────────────────────────────────

const ORE_ROWS: readonly [string, string, string, number, string][] = [
  ['ferrite', 'Ferrite', 'Copper', 1, '#c9875a'],
  ['cuprite', 'Cuprite', 'Iron', 2, '#9aa7bd'],
  ['silica', 'Silica', 'Lead', 4, '#7d8aa6'],
  ['cobaltite', 'Cobaltite', 'Silicon', 8, '#4f7dff'],
  ['lumen', 'Lumen Quartz', 'Aluminium', 17, '#fff2a8'],
  ['titanite', 'Titanite', 'Silver', 36, '#dfe8f5'],
  ['cryonite', 'Cryonite', 'Gold', 75, '#f5c542'],
  ['emberite', 'Emberite', 'Diamond', 160, '#7fe6ff'],
  ['chronite', 'Chronite', 'Platinum', 340, '#c7a7ff'],
  ['prismite', 'Prismite', 'Titanium', 730, '#ff7aab'],
  ['umbralite', 'Umbralite', 'Iridium', 1600, '#7b6bf0'],
  ['voidglass', 'Voidglass', 'Palladium', 3500, '#ff3d7f'],
];

export const ORES: readonly ResourceDef[] = ORE_ROWS.map(([id, name, ipm, value, color], tier) => ({
  id, name, ipm, kind: 'ore', tier, value, color, glyph: '●',
}));

// ── Alloys (IPM bars) ──────────────────────────────────────────────────────

type AlloyRow = readonly [string, string, string, readonly Ingredient[], number, number, number, string];

const ALLOY_ROWS: readonly AlloyRow[] = [
  // id, name, IPM, inputs, smelt seconds, unlock GC, sell GC, colour
  ['ferrite_bar', 'Ferrite Bar', 'Copper Bar', [ing('ferrite', 1000)], 20, 0, 1450, '#c9875a'],
  ['cuprite_bar', 'Cuprite Bar', 'Iron Bar', [ing('cuprite', 1000)], 30, 3e3, 3000, '#9aa7bd'],
  ['silica_bar', 'Silica Bar', 'Lead Bar', [ing('silica', 1000)], 40, 9e3, 6100, '#7d8aa6'],
  ['cobalt_bar', 'Cobalt Bar', 'Silicon Bar', [ing('cobaltite', 1000)], 60, 25e3, 12500, '#4f7dff'],
  ['lumen_bar', 'Lumen Bar', 'Aluminium Bar', [ing('lumen', 1000)], 80, 75e3, 27600, '#fff2a8'],
  ['titan_bar', 'Titan Bar', 'Silver Bar', [ing('titanite', 1000)], 120, 225e3, 60000, '#dfe8f5'],
  ['cryo_bar', 'Cryo Bar', 'Gold Bar', [ing('cryonite', 1000)], 180, 500e3, 120000, '#f5c542'],
  ['duralloy', 'Duralloy', 'Bronze Bar', [ing('titan_bar', 2), ing('ferrite_bar', 10)], 240, 1e6, 234000, '#e0a36b'],
  ['hullsteel', 'Hullsteel', 'Steel Bar', [ing('silica_bar', 15), ing('cuprite_bar', 30)], 480, 2e6, 340000, '#b6c3d6'],
  ['chrono_bar', 'Chrono Bar', 'Platinum Bar', [ing('cryo_bar', 2), ing('chronite', 1000)], 600, 4e6, 780000, '#c7a7ff'],
  ['prism_bar', 'Prism Bar', 'Titanium Bar', [ing('duralloy', 2), ing('prismite', 1000)], 720, 8e6, 1630000, '#ff7aab'],
  ['umbral_bar', 'Umbral Bar', 'Iridium Bar', [ing('hullsteel', 2), ing('umbralite', 1000)], 840, 15e6, 3110000, '#7b6bf0'],
  ['void_bar', 'Void Bar', 'Palladium Bar', [ing('chrono_bar', 2), ing('voidglass', 1000)], 960, 30e6, 7e6, '#ff3d7f'],
];

// ── Items (IPM items under IdleVoid's Anomaly names) ───────────────────────

type ItemRow = readonly [string, string, string, string, Rarity, readonly Ingredient[], number, number, number, readonly FleetStat[]];

const ITEM_ROWS: readonly ItemRow[] = [
  // id, name, IPM, glyph, rarity, inputs, craft seconds, unlock GC, sell GC, fitting stats
  ['hull_plating', 'Hull Plating', 'Copper Wire', '▦', 'common', [ing('ferrite_bar', 5)], 60, 0, 10e3, ['cargo']],
  ['velocity_chip', 'Velocity Chip', 'Iron Nails', '⟫', 'common', [ing('cuprite_bar', 5)], 120, 20e3, 20e3, ['ship']],
  ['phase_cap', 'Phase Capacitor', 'Battery', '▮', 'common', [ing('hull_plating', 2), ing('ferrite_bar', 10)], 240, 50e3, 70e3, ['smeltYield']],
  ['ablative_layer', 'Ablative Layer', 'Hammer', '⛨', 'common', [ing('velocity_chip', 2), ing('silica_bar', 5)], 480, 100e3, 135e3, ['discount']],
  ['sensor_array', 'Sensor Array', 'Glass', '◎', 'common', [ing('cobalt_bar', 10)], 720, 200e3, 220e3, ['mine']],
  ['flux_coil', 'Flux Coil', 'Circuit', '@', 'common', [ing('cobalt_bar', 5), ing('lumen_bar', 5), ing('hull_plating', 10)], 1200, 400e3, 620e3, ['smelt']],
  ['targeting_suite', 'Targeting Suite', 'Lens', '⌖', 'common', [ing('sensor_array', 1), ing('titan_bar', 5)], 2400, 1e6, 1.1e6, ['craftYield']],
  ['plasma_core', 'Plasma Core', 'Laser', '⚛', 'common', [ing('cryo_bar', 5), ing('targeting_suite', 1), ing('cuprite_bar', 10)], 3600, 2e6, 3.2e6, ['mine']],
  ['salvage_node', 'Salvage Node', 'Basic Computer', '♻', 'common', [ing('flux_coil', 5), ing('titan_bar', 5)], 4800, 5e6, 7.6e6, ['sell']],
  ['recovery_proto', 'Recovery Protocol', 'Solar Panel', '♥', 'common', [ing('flux_coil', 5), ing('sensor_array', 10)], 6000, 10e6, 12.5e6, ['craft']],
  ['warp_edge', 'Warp Edge', 'Laser Torch', '†', 'common', [ing('duralloy', 5), ing('plasma_core', 2), ing('targeting_suite', 5)], 7200, 20e6, 31e6, ['ship']],
  ['fold_resonator', 'Fold Resonator', 'Advanced Battery', '⬡', 'common', [ing('hullsteel', 20), ing('phase_cap', 30)], 9000, 30e6, 35e6, ['te']],
  ['overcharge', 'Overcharge', 'Thermal Scanner', '🜂', 'rare', [ing('chrono_bar', 5), ing('plasma_core', 2), ing('sensor_array', 5)], 10800, 50e6, 71.5e6, ['smelt', 'smeltYield']],
  ['tactical_array', 'Tactical Array', 'Advanced Computer', '⌬', 'rare', [ing('prism_bar', 5), ing('salvage_node', 5)], 12600, 120e6, 180e6, ['mine', 'cargo']],
  ['neural_iface', 'Neural Interface', 'Navigation Module', '✺', 'rare', [ing('overcharge', 1), ing('warp_edge', 2)], 13500, 250e6, 1e9, ['craft', 'ship']],
  ['combat_surge', 'Combat Surge', 'Plasma Torch', '✷', 'rare', [ing('umbral_bar', 15), ing('warp_edge', 5)], 15000, 550e6, 1.15e9, ['craft', 'craftYield']],
  ['deep_signal', 'Deep Signal', 'Radio Tower', '⌇', 'rare', [ing('chrono_bar', 75), ing('lumen_bar', 150), ing('prism_bar', 50)], 15600, 1.5e9, 1.45e9, ['mine', 'sell']],
  ['event_horizon', 'Event Horizon', 'Telescope', '◉', 'void', [ing('targeting_suite', 20), ing('tactical_array', 1)], 16800, 5e9, 2.7e9, ['cargo', 'ship']],
  ['pressure_wave', 'Pressure Wave', 'Satellite Dish', '≋', 'void', [ing('hullsteel', 150), ing('void_bar', 30)], 18000, 17.5e9, 3.4e9, ['ship', 'cargo']],
  ['iron_nexus', 'Iron Nexus', 'Motor', '⚙', 'rare', [ing('duralloy', 500), ing('ablative_layer', 200)], 19200, 60e9, 7e9, ['discount', 'mine']],
  ['resonance_engine', 'Resonance Engine', 'Wind Turbine', '◈', 'rare', [ing('lumen_bar', 300), ing('iron_nexus', 1)], 21600, 500e9, 140e9, ['smelt', 'craft']],
  ['temporal_echo', 'Temporal Echo', 'Space Probe', '⧗', 'void', [ing('pressure_wave', 1), ing('event_horizon', 1), ing('recovery_proto', 25)], 22200, 1e12, 180e9, ['haste']],
];

export const RARITY_COLOR: Readonly<Record<Rarity, string>> = {
  common: '#a9b8d6',
  rare: '#4dd9f5',
  void: '#ff3d7f',
};

export const RARITY_LABEL: Readonly<Record<Rarity, string>> = {
  common: 'Common',
  rare: 'Rare',
  void: 'Void',
};

export const ALLOYS: readonly ResourceDef[] = ALLOY_ROWS.map(([id, name, ipm, inputs, time, unlock, value, color], tier) => ({
  id, name, ipm, kind: 'alloy', tier, value, color, glyph: '■', recipe: { inputs, time, unlock },
}));

export const ITEMS: readonly ResourceDef[] = ITEM_ROWS.map(([id, name, ipm, glyph, rarity, inputs, time, unlock, value, fits], tier) => ({
  id, name, ipm, kind: 'item', tier, value, glyph, rarity, fits, color: RARITY_COLOR[rarity], recipe: { inputs, time, unlock },
}));

export const ALL_RESOURCES: readonly ResourceDef[] = [...ORES, ...ALLOYS, ...ITEMS];

const BY_ID = new Map(ALL_RESOURCES.map((d) => [d.id, d]));

export function resource(id: string): ResourceDef {
  const def = BY_ID.get(id);
  if (!def) throw new Error(`Unknown resource: ${id}`);
  return def;
}

for (const def of ALL_RESOURCES) {
  for (const i of def.recipe?.inputs ?? []) if (!BY_ID.has(i.id)) throw new Error(`${def.id}: unknown input ${i.id}`);
}

// ── Fittings (GDD §3.5) ────────────────────────────────────────────────────

/** Cumulative copies for each fitting level — IdleVoid's Anomaly thresholds. */
export const FIT_THRESHOLDS: readonly number[] = [0, 1, 3, 7, 15, 31, 55, 80];
export const FIT_MAX_LEVEL = FIT_THRESHOLDS.length - 1;

/** Bonus per effect at levels 1–7. */
export const FIT_VALUES: Readonly<Record<Rarity, readonly number[]>> = {
  common: [0.05, 0.08, 0.11, 0.14, 0.17, 0.21, 0.25],
  rare: [0.04, 0.07, 0.10, 0.13, 0.16, 0.19, 0.22],
  void: [0.08, 0.13, 0.18, 0.24, 0.30, 0.36, 0.42],
};

export function fitLevel(copies: number): number {
  let level = 0;
  for (let l = 1; l <= FIT_MAX_LEVEL; l += 1) if (copies >= FIT_THRESHOLDS[l]!) level = l;
  return level;
}

export function fitBonus(rarity: Rarity, level: number): number {
  return level <= 0 ? 0 : FIT_VALUES[rarity][Math.min(level, FIT_MAX_LEVEL) - 1]!;
}

/** SC paid the first time each item is crafted — discovery is an SC source (GDD §2). */
export const DISCOVERY_SC: Readonly<Record<Rarity, number>> = { common: 5, rare: 15, void: 50 };

/** Yield and discount caps: yield is the chance of a bonus unit, discount cuts planet costs. */
export const YIELD_CAP = 1;
export const DISCOUNT_CAP = 0.6;

export const FREE_SOCKETS = 3;
export const MAX_SOCKETS = 12;

/** SC for the next socket, given how many are owned — IdleVoid's curve. Null at the cap. */
export function socketCost(owned: number): number | null {
  if (owned >= MAX_SOCKETS) return null;
  if (owned < FREE_SOCKETS) return 0;
  if (owned <= 5) return [50, 100, 150][owned - 3]!;
  if (owned <= 8) return [200, 300, 400][owned - 6]!;
  return 500;
}
